import { Fragment, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import type { FlatProduct } from "../types";
import type { MonitorColorBy } from "../../../api/monitors";
import ProductRow from "./ProductRow";
import OrderCommentRow from "./OrderCommentRow";
import { enhanceColor, rowColorFor } from "./rowColors";
import { useIsDarkTheme } from "../../../store/hooks";

interface SelectedProduct {
  itemId: number;
  billId: number;
  billElementId: number;
  productKey: string;
}

interface MonitorsListViewProps {
  flatProducts: FlatProduct[];
  selectedProducts: SelectedProduct[];
  aggregatedTotals: Record<number, number>;
  aggregatedQuantities: boolean;
  formatTimeFromISO: (isoString: string) => string;
  calculateElapsedTime: (orderTime: string) => string;
  onProductClick: (product: FlatProduct) => void;
  // Клик по фона (извън редовете) — сваля селекцията.
  onBackgroundClick: () => void;
  // По какво се оцветяват редовете (настройка на монитора).
  colorBy: MonitorColorBy;
  // Кои статуси значат „изпълнено" на този екран ("2" обикновен, "3" шпикер).
  fulfilledStatuses: string[];
}

type TreeMeta = {
  mainHasAdditions: boolean;
  isLastAdditionInGroup: boolean;
};

const MonitorsListView = ({
  flatProducts,
  selectedProducts,
  aggregatedTotals,
  aggregatedQuantities,
  formatTimeFromISO,
  calculateElapsedTime,
  onProductClick,
  onBackgroundClick,
  colorBy,
  fulfilledStatuses,
}: MonitorsListViewProps) => {
  const { t } = useTranslation();
  // Пастелите са реални hex стойности (enhanceColor смята върху тях) →
  // тъмният набор се избира тук, не през CSS променлива.
  const isDark = useIsDarkTheme();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Предварително изчисляваме tree позицията за всеки ред: дали основният продукт,
  // към който принадлежи, има добавки и дали този ред (ако е добавка) е последната
  // добавка в групата. Нужно е за isLast в релсите и за "through rail"-а.
  const treeMetaByIndex = useMemo<TreeMeta[]>(() => {
    const metas: TreeMeta[] = flatProducts.map(() => ({
      mainHasAdditions: false,
      isLastAdditionInGroup: false,
    }));
    let i = 0;
    while (i < flatProducts.length) {
      const main = flatProducts[i];
      if (main.isAddition) {
        i++;
        continue;
      }
      // Намираме последователните добавки в рамките на същата поръчка
      let lastAddIdx = -1;
      let j = i + 1;
      while (
        j < flatProducts.length &&
        flatProducts[j].orderKey === main.orderKey &&
        flatProducts[j].isAddition
      ) {
        lastAddIdx = j;
        j++;
      }
      const hasAdditions = lastAddIdx !== -1;
      metas[i] = { mainHasAdditions: hasAdditions, isLastAdditionInGroup: false };
      for (let k = i + 1; k <= (hasAdditions ? lastAddIdx : i); k++) {
        metas[k] = {
          mainHasAdditions: hasAdditions,
          isLastAdditionInGroup: k === lastAddIdx,
        };
      }
      i = hasAdditions ? lastAddIdx + 1 : i + 1;
    }
    return metas;
  }, [flatProducts]);

  // Автоматично скролираме селектирания ред във видимата зона
  useEffect(() => {
    if (selectedProducts.length === 0) return;
    const lastKey = selectedProducts[selectedProducts.length - 1].productKey;
    const row = rowRefs.current[lastKey];
    if (row) {
      row.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [selectedProducts]);

  // Ако поръчките са от повече от 1 ден, показваме и датата в колона "Поръчано".
  const hasMultipleDays = useMemo(() => {
    const days = new Set<string>();
    for (const p of flatProducts) {
      try {
        const d = new Date(p.time);
        days.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
        if (days.size > 1) return true;
      } catch {
        // ignore unparsable
      }
    }
    return false;
  }, [flatProducts]);

  const formatOrderTime = (isoString: string): string => {
    if (!hasMultipleDays) return formatTimeFromISO(isoString);
    try {
      const date = new Date(isoString);
      const dd = date.getDate().toString().padStart(2, "0");
      const mm = (date.getMonth() + 1).toString().padStart(2, "0");
      return `${dd}.${mm} ${formatTimeFromISO(isoString)}`;
    } catch {
      return formatTimeFromISO(isoString);
    }
  };

  // Grid columns: status · table · product · elapsed · qty · total? · operator · order time.
  // Колоните зависят от настройки/данни → gridTemplateColumns остава inline style.
  const orderTimeCol = hasMultipleDays ? "120px" : "90px";
  const gridTemplate = aggregatedQuantities
    ? `48px 90px minmax(260px, 1fr) 80px 90px 90px 140px ${orderTimeCol}`
    : `48px 90px minmax(260px, 1fr) 80px 90px 140px ${orderTimeCol}`;

  return (
    <div
      ref={scrollerRef}
      className="h-full min-h-0 overflow-y-auto overflow-x-hidden overscroll-none rounded-[12px] border border-shade-2 bg-surface"
      // Кликовете по редовете спират bubbling-а → дотук стига само фонът
      onClick={onBackgroundClick}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-[2] grid gap-x-[10px] border-b border-shade-2 bg-panel px-[14px] py-[10px] text-[10.5px] font-bold uppercase tracking-[1.2px] text-muted"
        style={{ gridTemplateColumns: gridTemplate }}
      >
        <span className="text-center">{t("monitors.colStatus")}</span>
        <span>{t("monitors.colTable")}</span>
        <span>{t("monitors.colProduct")}</span>
        <span className="text-right">{t("monitors.colElapsed")}</span>
        <span className="text-right">{t("monitors.colQty")}</span>
        {aggregatedQuantities && (
          <span className="text-center">{t("monitors.colTotal")}</span>
        )}
        <span>{t("monitors.colOperator")}</span>
        <span className="text-right">{t("monitors.colOrderTime")}</span>
      </div>

      {/* Rows */}
      <div>
        {flatProducts.map((product, rowIndex) => {
          const productKey = `${product.orderKey}-${product.productIndex}`;
          const isSelected = selectedProducts.some(
            (p) => p.productKey === productKey,
          );

          const orderProducts = flatProducts.filter(
            (p) => p.orderKey === product.orderKey,
          );
          const isOrderCompleted = orderProducts.every(
            (p) => p.isCancelled || fulfilledStatuses.includes(p.status),
          );
          const elapsed = !isOrderCompleted
            ? calculateElapsedTime(product.time)
            : null;

          // Цвят на реда според настройката „Оцветяване" (маса/сервитьор/без)
          const baseColor = rowColorFor(
            colorBy,
            product.tableName,
            product.waiterName,
            isDark,
          );
          const prev = rowIndex > 0 ? flatProducts[rowIndex - 1] : null;
          const next =
            rowIndex < flatProducts.length - 1
              ? flatProducts[rowIndex + 1]
              : null;
          const shouldEnhance =
            (product.hasAdditions &&
              next?.isAddition &&
              next?.orderKey === product.orderKey) ||
            (product.isAddition &&
              (prev?.hasAdditions || prev?.isAddition) &&
              prev?.orderKey === product.orderKey);
          const rowBackground =
            baseColor && shouldEnhance ? enhanceColor(baseColor) : baseColor;

          // Начало на нова поръчка: тънка междина от предишната (без първата)
          // и лента с коментара над първия ред, ако поръчката има коментар
          const startsOrder = prev?.orderKey !== product.orderKey;

          return (
            <Fragment key={productKey}>
              {startsOrder && rowIndex > 0 && (
                <div className="h-[5px] bg-canvas" aria-hidden />
              )}
              {startsOrder && product.comment && (
                <OrderCommentRow
                  gridTemplate={gridTemplate}
                  tableLabel={
                    product.oldTableName
                      ? `#${product.oldTableName} » ${product.tableName}`
                      : `#${product.tableName}`
                  }
                  comment={product.comment}
                />
              )}
              <ProductRow
                product={product}
                gridTemplate={gridTemplate}
                isSelected={isSelected}
                rowBackground={rowBackground}
                isLastAdditionInGroup={
                  treeMetaByIndex[rowIndex]?.isLastAdditionInGroup ?? false
                }
                mainHasAdditions={
                  treeMetaByIndex[rowIndex]?.mainHasAdditions ?? false
                }
                aggregatedQuantities={aggregatedQuantities}
                aggregatedTotal={aggregatedTotals[product.itemId]}
                fulfilledStatuses={fulfilledStatuses}
                elapsed={elapsed}
                orderTimeLabel={formatOrderTime(product.time)}
                onClick={() => onProductClick(product)}
                rowRef={(el) => {
                  rowRefs.current[productKey] = el;
                }}
              />
            </Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default MonitorsListView;
